import { Add } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { FormControl, FormControlLabel, Grid, RadioGroup } from "@mui/material";
import { Box, Button, FormField, Radio, SingleImageUploader, Typography } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import PopupAddLinkCourse from "./popupAddLinkCourse";
import ResourceService from "../../services/identity/Resource.service";

import { IRecordResource } from "../../commons/interfaces";
import { DiscussionGroupAccess, Mode } from "../../constants/enums";
import { IDataChannel, IErrorChannel } from "../../containers/channel/useChannel";

export const BasicInfoChannel = (props: {
    mode: Mode;
    title: string;
    dataError: IErrorChannel;
    dataDetail: IDataChannel;
    onChangeValue: (newValue: any, key: string) => void;
}) => {
    const Strings = useStrings();

    const [openLink, setOpenLink] = useState<boolean>(false);
    const [itemLinked, setItemLinked] = useState<IRecordResource | undefined>(undefined);

    useEffect(() => {
        if (
            (props.mode === Mode.Update || props.mode === Mode.View) &&
            !Helpers.isNullOrEmpty(props.dataDetail?.referenceId) &&
            props.dataDetail?.referenceId !== "0"
        ) {
            (async () => {
                try {
                    const result = await ResourceService.getDetail(props.dataDetail?.referenceId);
                    setItemLinked(result);
                } catch (error) { }
            })();
        }
    }, [props.mode, props.dataDetail?.referenceId]);

    return (
        <Box p={2}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box display="grid">
                        <Typography variant="h4" sx={{ mb: 1 }}>
                            {props.title}
                        </Typography>
                        <Typography variant="caption" color="secondary" fontWeight="regular">
                            {Strings.CHANNEL.NOTE_TITLE}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={12}>
                    <FormControl disabled={props.mode === Mode.View}>
                        <RadioGroup
                            row
                            value={props.dataDetail?.accessMode || DiscussionGroupAccess.Public}
                            onChange={(event: any, value: any) => {
                                props.onChangeValue(event.target?.value, "accessMode");
                            }}
                        >
                            <FormControlLabel
                                control={<Radio />}
                                sx={{ mb: 0, mt: 0, ml: 0 }}
                                label={Strings.Common.Public}
                                value={DiscussionGroupAccess.Public}
                            />
                            <FormControlLabel
                                control={<Radio />}
                                sx={{ mb: 0, mt: 0, ml: 0 }}
                                label={Strings.Common.Private}
                                value={DiscussionGroupAccess.Private}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                    <FormField
                        required
                        maxLength={25}
                        mode={props.mode}
                        variant="outlined"
                        label={Strings.CHANNEL.CHANNEL_NAME}
                        placeholder={Strings.CHANNEL.ENTER_NAME}
                        errorMessage={props.dataError?.name}
                        defaultValue={props.dataDetail?.name}
                        onBlur={(value: any) => {
                            props.onChangeValue(value, "name");
                        }}
                    />
                    {props.mode !== Mode.View && (
                        <Typography variant="caption" color="secondary" fontWeight="regular">
                            {Strings.CHANNEL.MAX_24_CHAR}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} md={12}>
                    <FormField
                        required
                        multiline
                        maxLength={255}
                        mode={props.mode}
                        variant="outlined"
                        label={Strings.CHANNEL.CHANNEL_DESCRIPTION}
                        placeholder={Strings.CHANNEL.ENTER_DESCRIPTION}
                        errorMessage={props.dataError?.description}
                        defaultValue={props.dataDetail?.description}
                        onBlur={(value: any) => {
                            props.onChangeValue(value, "description");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={12}>
                    <Box mb={1} display="grid">
                        <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 0, fontSize: "0.825rem", mb: 1.25 }}>
                            {Strings.CHANNEL.IMAGE}
                        </Typography>
                        {props.mode !== Mode.View && (
                            <Typography variant="caption" color="secondary" fontWeight="regular">
                                {Strings.CHANNEL.NOTE_IMG}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        <SingleImageUploader
                            label={""}
                            title={""}
                            description={""}
                            mode={props.mode}
                            folderPath={"/Channel/temp"}
                            fileId={props.dataDetail?.avatarImage}
                            onChangeImage={(res) => {
                                props.onChangeValue(res, "avatarImage");
                            }}
                        />
                    </Box>
                    {props.mode !== Mode.View && (
                        <Box mt={1}>
                            <Typography variant="caption" color="secondary" fontWeight="regular">
                                {Strings.CHANNEL.IMG_169}
                            </Typography>
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12} md={12}>
                    <Box display="grid">
                        <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 0, fontSize: "0.825rem", mb: 1.25 }}>
                            {Strings.CHANNEL.LINK_COURSE}
                        </Typography>
                        {props.mode !== Mode.View && (
                            <Typography variant="caption" color="secondary" fontWeight="regular">
                                {Strings.CHANNEL.NOTE_LINK_COURSE}
                            </Typography>
                        )}
                    </Box>

                    <PopupAddLinkCourse
                        open={openLink}
                        onClose={() => {
                            setOpenLink(false);
                        }}
                        idSelected={props.dataDetail?.referenceId}
                        onAction={(data) => {
                            if (data?.id !== props.dataDetail?.referenceId) {
                                setItemLinked(data);
                                props.onChangeValue(data?.id, "referenceId");
                            }
                            setOpenLink(false);
                        }}
                    />
                    {!Helpers.isNullOrEmpty(props.dataDetail?.referenceId) && props.dataDetail?.referenceId !== "0" && (
                        <Box p={2}>
                            <Typography variant="button" fontWeight="bold">
                                {`${itemLinked?.resourceCode || ""} - ${Helpers.getDefaultValueMultiLanguage(itemLinked?.resourceName, "vi")}`}
                            </Typography>
                        </Box>
                    )}

                    {(props.mode === Mode.Create ||
                        (props.mode === Mode.Update &&
                            (props.dataDetail?.referenceId === "0" || Helpers.isNullOrEmpty(props.dataDetail?.referenceId)))) && (
                            <Box mt={1} display="flex" alignItems="center">
                                <Button
                                    variant="outlined"
                                    color="info"
                                    onClick={() => {
                                        setOpenLink(true);
                                    }}
                                >
                                    <Add /> &nbsp;{" "}
                                    {Helpers.isNullOrEmpty(props.dataDetail?.referenceId) && props.dataDetail?.referenceId === "0"
                                        ? Strings.CHANNEL.ADD_NEW_LINK_COURSE
                                        : Strings.CHANNEL.CHANGE_LINK_COURSE}
                                </Button>
                                {!Helpers.isNullOrEmpty(props.dataDetail?.referenceId) && props.dataDetail?.referenceId !== "0" && (
                                    <Button
                                        color="error"
                                        sx={{ ml: 1 }}
                                        variant="outlined"
                                        onClick={() => {
                                            setItemLinked(undefined);
                                            props.onChangeValue(undefined, "referenceId");
                                        }}
                                    >
                                        {Strings.CHANNEL.UN_LINK_COURSE}
                                    </Button>
                                )}
                            </Box>
                        )}
                </Grid>
            </Grid>
        </Box>
    );
};
